import { ORPCError } from '@orpc/client'
import type { HttpError } from '@refinedev/core'
import type { ErrorParams } from '@sirius/contracts/admin'
import { errorText, issueText, paramsOf } from '~/i18n/errorText'

/**
 * An issue of a configuration's settings as the API sends it: `code` and
 * `params` are absent from a backend older than the codes, then `message`
 * (English) is all there is.
 */
export interface ReportedIssue {
  path: (string | number)[]
  message: string
  code?: string
  params?: ErrorParams
}

type Issue = {
  path?: readonly PropertyKey[]
  message: string
  code?: unknown
  params?: unknown
}

function issuesOf(data: unknown): Issue[] {
  if (
    typeof data !== 'object' ||
    data === null ||
    !('issues' in data) ||
    !Array.isArray(data.issues)
  ) {
    return []
  }
  return data.issues as Issue[]
}

/** `errorCode` and `params` of an API error's `data` (`errorCode` absent for oRPC's own errors). */
function detailOf(data: unknown): {
  errorCode?: string
  params?: ErrorParams
} {
  if (typeof data !== 'object' || data === null) {
    return {}
  }
  return {
    errorCode:
      'errorCode' in data && typeof data.errorCode === 'string'
        ? data.errorCode
        : undefined,
    params: 'params' in data ? paramsOf(data.params) : undefined,
  }
}

/** The `errorCode` an API error carries, if any. */
export function errorCodeOf(error: unknown): string | undefined {
  return error instanceof ORPCError ? detailOf(error.data).errorCode : undefined
}

/** The panel-language text of an API error: its `errorCode`'s, else the server's English message. */
function apiErrorText(error: ORPCError<string, unknown>): string {
  const { errorCode, params } = detailOf(error.data)
  return errorText(errorCode, params, error.message)
}

/**
 * oRPC errors as Refine HttpErrors, their `message` in the panel's language.
 * `errors` (field → message) makes @refinedev/react-hook-form show
 * server-side validation on the fields.
 */
export function toHttpError(error: unknown): unknown {
  if (!(error instanceof ORPCError)) {
    return error
  }
  const message = apiErrorText(error)
  const httpError: HttpError = {
    statusCode: error.status,
    message,
  }
  const errors: Record<string, string> = {}
  for (const issue of issuesOf(error.data)) {
    const field = issue.path?.[0]
    if (typeof field === 'string' && !Object.hasOwn(errors, field)) {
      errors[field] = issueText(issue)
    }
  }
  const { errorCode } = detailOf(error.data)
  if (
    errorCode === 'TENANT_SLUG_TAKEN' ||
    errorCode === 'INVALID_TENANT_SLUG'
  ) {
    errors.slug ??= message
  }
  // an unknown game id (the API checks them before it writes a cabinet)
  if (errorCode === 'UNKNOWN_GAMES') {
    errors.gameIds ??= message
  }
  return Object.keys(errors).length > 0 ? { ...httpError, errors } : httpError
}

function isPathKey(key: unknown): key is string | number {
  return typeof key === 'string' || typeof key === 'number'
}

/**
 * The issues of a configuration's settings the API refused
 * (`games.configurations.publish`, `analysis.start`: BAD_REQUEST,
 * `data.issues` at paths under `settings`), each at its path in the
 * settings (`['reels', 0, 3]`; `[]` for the settings themselves). Empty for
 * any other error. Issues of other fields (`name`, `note`) are left to
 * `toHttpError`'s field errors.
 */
export function settingsIssues(error: unknown): ReportedIssue[] {
  if (!(error instanceof ORPCError) || error.code !== 'BAD_REQUEST') {
    return []
  }
  return issuesOf(error.data).flatMap((issue) => {
    const path = issue.path ?? []
    // the server's data, unchecked: skip what is not an issue
    const message: unknown = issue.message
    const code: unknown = issue.code
    return path[0] === 'settings' &&
      path.every(isPathKey) &&
      typeof message === 'string'
      ? [
          {
            path: path.slice(1),
            message,
            // the English message and its code and params: the page words
            // them in its language (`configIssueText`)
            ...(typeof code === 'string' ? { code } : {}),
            ...(paramsOf(issue.params) === undefined
              ? {}
              : { params: paramsOf(issue.params) }),
          },
        ]
      : []
  })
}

/** The message of an oRPC error (as `toHttpError` words it) or any error. */
export function errorMessage(error: unknown): string | undefined {
  const httpError = toHttpError(error)
  return typeof httpError === 'object' &&
    httpError !== null &&
    'message' in httpError &&
    typeof httpError.message === 'string'
    ? httpError.message
    : undefined
}

/** A 401 from oRPC (`status`) or a Refine HttpError (`statusCode`). */
export function isUnauthorized(error: unknown): boolean {
  if (error instanceof ORPCError) {
    return error.status === 401
  }
  return (
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    error.statusCode === 401
  )
}
